import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, DialogContent, Divider } from '@mui/material';
import {
    uploadFile,
    resetFileState as resetImportFiles,
    invalidFilesSelector,
    uploadActiveSelector,
    uploadFinishedSelector,
    precheckActiveSelector,
} from '../../../../redux/import/sliceImportFileUpload';
import { useDispatch, useSelector } from 'react-redux';
import store, { IAppReduxStore } from '../../../../redux/store';
import { GlobalVariables } from '../../../../GlobalVariables';
import { headingFontBold, vars } from '../../../../Theme';
import { useParams } from 'react-router-dom';
import {
    ImportDialogStep,
    resetState as resetImportDialog,
    setCategoryChecked,
    setIsHidden as setUploadDialogHidden,
    setPackageChecked,
    setStep,
} from '../../../../redux/import/sliceImportDialog';
import DocTypeSelectionView from './DocTypeSelectionView';
import TreeSelectionView from './TreeSelectionView';
import FileUploadView from './FileUploadView';
import DialogHeader from '../_parts/DialogHeader';
import DialogFrame from '../_parts/DialogFrame';
import { renderIf } from '../../../../util/ReactUtils';
import { ContentLayout } from '../_parts/ContentLayout';
import CtaButton from '../_parts/CtaButton';

interface IUploadDialogProps {
    onClose: () => void;
}

export default (props: IUploadDialogProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'uploadDialog' });
    const { replicaId } = useParams();
    const dispatch = useDispatch();

    const { prevStep, currentStep, nextStep } = useSelector(
        (_store: IAppReduxStore) => _store.importDialog,
    );
    const viewName = useSelector((_store: IAppReduxStore) => _store.importData.currentView);
    const selectedNode = useSelector(
        (_store: IAppReduxStore) => _store.docnumberSidepanel.selectedNode,
    );
    const { hidden, selectedDocType, checkedCategories, checkedPackages } = useSelector(
        (_store: IAppReduxStore) => _store.importDialog,
    );
    const files = useSelector((_store: IAppReduxStore) => _store.projectFileUpload.fileModels);
    const invalidFiles = useSelector(invalidFilesSelector);
    const structures = useSelector(
        (_store: IAppReduxStore) => _store.importViewData.viewStructures.data,
    );
    const precheckLoading = useSelector(precheckActiveSelector);
    const categoryMandatory = useMemo(
        () => selectedDocType?.isCategoryMandatory,
        [selectedDocType],
    );
    const packageMandatory = useMemo(() => selectedDocType?.isPackageMandatory, [selectedDocType]);
    const uploadActive = useSelector(uploadActiveSelector);
    const uploadFinished = useSelector(uploadFinishedSelector);

    useEffect(() => {
        if (selectedNode && viewName === 'Category') {
            dispatch(setCategoryChecked([selectedNode, true]));
        }

        if (selectedNode && viewName === 'Package') {
            dispatch(setPackageChecked([selectedNode, true]));
        }
    }, []);

    const onHide = () => {
        dispatch(setUploadDialogHidden(true));
    };

    const onClose = () => {
        dispatch(resetImportDialog());
        dispatch(resetImportFiles());
        GlobalVariables.resetFiles();
        props.onClose();
    };

    useEffect(() => {
        dispatch(setStep(['next', getNextStep()]));
        dispatch(setStep(['prev', getPrevStep()]));
    }, [currentStep, categoryMandatory, packageMandatory]);

    useEffect(() => {
        uploadFinished && hidden && setTimeout(() => onClose(), 500);
    }, [uploadFinished]);

    const uploadFiles = async () => {
        const notUploaded = files.slice().filter(model => !model.uploadState.isUploaded);

        await Promise.all(
            notUploaded.map(async model => {
                await store.dispatch(
                    uploadFile({
                        replicaId: replicaId,
                        modelId: model.id,
                        file: GlobalVariables.getFileById(model.id),
                        docType: selectedDocType.name,
                        categories:
                            structures['Category']?.filter(ctg =>
                                checkedCategories.includes(ctg.IdDocIndex),
                            ) ?? [],
                        packages:
                            structures['Package']?.filter(pkg =>
                                checkedPackages.includes(pkg.IdDocIndex),
                            ) ?? [],
                    }),
                );
            }),
        );
    };

    const renderBackButton = () => (
        <Button
            variant='outlined'
            sx={{ color: vars.brandGreen, ...headingFontBold, textTransform: 'none' }}
            onClick={() => dispatch(setStep(['current', prevStep]))}
        >
            {t('back')}
        </Button>
    );

    const nextButtonEnabled = {
        [ImportDialogStep.DoctypeSelection]: () =>
            files?.length > 0 &&
            !precheckLoading &&
            invalidFiles?.length === 0 &&
            Boolean(selectedDocType),
        [ImportDialogStep.CategorySelection]: () =>
            !categoryMandatory || checkedCategories?.length > 0,
        [ImportDialogStep.PackageSelection]: () => !packageMandatory || checkedPackages?.length > 0,
    };

    const renderNextButton = () => {
        const enabled = nextButtonEnabled[currentStep]?.();
        return (
            <Button
                disabled={!enabled}
                variant='contained'
                sx={{ color: 'white', ...headingFontBold, textTransform: 'none' }}
                onClick={() => {
                    if (nextStep === ImportDialogStep.UploadProcess && !uploadFinished) {
                        onHide();
                        uploadFiles();
                    }
                    dispatch(setStep(['current', nextStep]));
                }}
            >
                {t(nextStep === ImportDialogStep.UploadProcess ? 'upload' : 'next')}
            </Button>
        );
    };

    const getNextStep = (): ImportDialogStep => {
        if (currentStep < ImportDialogStep.CategorySelection && categoryMandatory)
            return ImportDialogStep.CategorySelection;
        if (currentStep < ImportDialogStep.PackageSelection && packageMandatory)
            return ImportDialogStep.PackageSelection;
        return ImportDialogStep.UploadProcess;
    };

    const getPrevStep = (): ImportDialogStep => {
        if (currentStep > ImportDialogStep.PackageSelection && packageMandatory)
            return ImportDialogStep.PackageSelection;
        if (currentStep > ImportDialogStep.CategorySelection && categoryMandatory)
            return ImportDialogStep.CategorySelection;
        return ImportDialogStep.DoctypeSelection;
    };

    const renderConfirmButton = () => (
        <Button
            disabled={uploadActive}
            variant='contained'
            sx={{ color: 'white', ...headingFontBold, textTransform: 'none' }}
            loading={uploadActive}
            onClick={() => onClose()}
        >
            {t('ok')}
        </Button>
    );

    const pageComponent = {
        [ImportDialogStep.DoctypeSelection]: <DocTypeSelectionView />,
        [ImportDialogStep.CategorySelection]: renderIf(
            categoryMandatory,
            <TreeSelectionView mode='Category' />,
        ),
        [ImportDialogStep.PackageSelection]: renderIf(
            packageMandatory,
            <TreeSelectionView mode='Package' />,
        ),
        [ImportDialogStep.UploadProcess]: <FileUploadView />,
    };

    const handleClose = () => {
        uploadActive ? onHide() : onClose();
    };

    return hidden ? (
        <></>
    ) : (
        <DialogFrame open>
            <DialogHeader
                variant={uploadActive ? 'minimize' : 'close'}
                dialogTitle={t('title')}
                onClose={handleClose}
            />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={null}
                    documentSelection={null}
                    main={pageComponent[currentStep]}
                />
            </DialogContent>
            <Divider />
            <DialogActions sx={{ paddingX: '24px' }}>
                <CtaButton text={t('cancel')} variant='text' onClick={handleClose} />
                {renderIf(
                    currentStep > ImportDialogStep.DoctypeSelection &&
                        !uploadActive &&
                        !uploadFinished,
                    renderBackButton(),
                )}
                {renderIf(currentStep < ImportDialogStep.UploadProcess, renderNextButton())}
                {renderIf(currentStep === ImportDialogStep.UploadProcess, renderConfirmButton())}
            </DialogActions>
        </DialogFrame>
    );
};
